<script>
  import { createEventDispatcher } from "svelte";
  import { clickOutside } from "$utils/clickOutside";
  import { Icon, ChevronDown, XMark } from "svelte-hero-icons";
  import { cx } from "$lib/utils/cx";

  export let value = [];
  export let label;
  export let options = [];
  export let optional = false;
  export let variant = "default";
  export let isReadonly = false;

  const dispatch = createEventDispatcher();
  let isOpen = false;

  $: selected = Array.isArray(value) ? value : [];
  $: selectedSet = new Set(selected);
  $: isObjectOptions = options.length > 0 && options[0] instanceof Object;
  $: normalizedOptions = isObjectOptions
    ? options
    : options.map((opt) => ({ value: opt, label: opt }));

  function labelFor(optionValue) {
    const match = normalizedOptions.find((option) => option.value === optionValue);
    return match ? match.label : optionValue;
  }

  function emit(next) {
    value = next;
    dispatch("change", next);
    dispatch("input", next);
  }

  function toggleOption(option) {
    if (isReadonly) return;
    if (selectedSet.has(option.value)) {
      emit(selected.filter((optionValue) => optionValue !== option.value));
    } else {
      emit([...selected, option.value]);
    }
  }

  function removeOption(optionValue) {
    if (isReadonly) return;
    emit(selected.filter((value) => value !== optionValue));
  }

  function clearValue() {
    if (isReadonly) return;
    emit([]);
  }

  function toggleDropdown() {
    isOpen = !isOpen;
  }

  function closeDropdown() {
    isOpen = false;
  }
</script>

<div class="flex flex-col basis-full {isReadonly ? 'opacity-60' : ''}">
  <div
    aria-label="Multi select"
    class="w-full flex items-center {$$props.class || ''}"
    use:clickOutside
    on:clickoutside={closeDropdown}
  >
    <div class="relative w-full">
      <button
        type="button"
        class="flex gap-2 items-center justify-between w-full min-h-8 text-left rounded p-2 pr-8 text-xs bg-surface-muted border-0 focus:outline-none truncate"
        class:cursor-not-allowed={isReadonly}
        class:focus:ring-2={!isReadonly}
        class:focus:ring-blue-500={!isReadonly}
        class:min-w-12={variant === "default"}
        class:min-w-32={variant === "wide"}
        class:min-w-40={variant === "ultrawide"}
        on:click={toggleDropdown}
        disabled={isReadonly}
      >
        <span
          class="text-xs text-content-subtle whitespace-nowrap pointer-events-none truncate"
          class:cursor-not-allowed={isReadonly}
        >
          {label}
        </span>

        <span class="text-xs text-content">
          {selected.length > 0 ? `${selected.length} selected` : ""}
        </span>
      </button>

      <Icon
        src={ChevronDown}
        class={cx(
          "absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-content-subtle pointer-events-none",
          isReadonly && "opacity-50",
        )}
      />
    </div>

    {#if optional && !isReadonly}
      <button
        type="button"
        class="ml-2 text-content-subtle hover:text-content focus:outline-none"
        on:click={clearValue}
      >
        <Icon src={XMark} class="w-4 h-4" />
      </button>
    {/if}

    <slot name="dynamic" />
  </div>

  {#if selected.length > 0}
    <div class="flex flex-wrap gap-1 mt-1">
      {#each selected as optionValue (optionValue)}
        <span
          class="inline-flex items-center gap-1 rounded bg-surface-muted px-1.5 py-0.5 text-xs text-content"
        >
          {labelFor(optionValue)}
          {#if !isReadonly}
            <button
              type="button"
              class="hover:text-content"
              on:click={() => removeOption(optionValue)}
            >
              <Icon src={XMark} class="w-3 h-3" />
            </button>
          {/if}
        </span>
      {/each}
    </div>
  {/if}

  <div
    class="z-50 mt-1 py-1 bg-surface border border-line-strong rounded-md shadow-lg max-h-40 overflow-auto top-full"
    class:hidden={!isOpen || isReadonly}
    class:mr-6={optional}
  >
    {#each normalizedOptions as option (option.value)}
      <button
        type="button"
        class="flex items-center gap-2 w-full px-2 py-1 text-left text-xs hover:bg-surface-hover focus:outline-none focus:bg-surface-muted"
        on:click={() => toggleOption(option)}
      >
        <span
          class="flex items-center justify-center w-4 h-4 text-[10px] text-white rounded border"
          class:bg-blue-500={selectedSet.has(option.value)}
          class:border-blue-500={selectedSet.has(option.value)}
          class:border-line-strong={!selectedSet.has(option.value)}
        >
          {selectedSet.has(option.value) ? "✓" : ""}
        </span>
        <span class="truncate">{option.label}</span>
      </button>
    {/each}
  </div>
</div>
